import { FilterReset } from '@carbon/react/icons';
import { Button } from '@carbon/react';
import { ReactNode } from 'react';
import { useDataTableContext } from '../context';
import styles from './filter-panel.module.scss';

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface FilterPanelProps {
  /** Content to render inside the filter panel */
  children: ReactNode;
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Collapsible filter panel container for the DataTable.
 * Displays filter controls in a sidebar and includes a reset button.
 * Visibility is controlled via DataTableContext.
 * @param {FilterPanelProps} props - Component props
 * @param {ReactNode} props.children - Filter controls to render inside the panel
 * @return {JSX.Element | null} The filter panel element or null when closed
 */
export function FilterPanel({ children }: FilterPanelProps) {
  // Get state from context - no prop drilling!
  const { activeFilterCount, isFilterPanelOpen, resetAllFilters } =
    useDataTableContext();

  // Don't render anything when closed - toolbar controls visibility
  if (!isFilterPanelOpen) {
    return null;
  }

  return (
    <div className={styles.panel}>
      <div className={styles.resetFilters}>
        <Button
          kind="ghost"
          size="lg"
          onClick={resetAllFilters}
          disabled={activeFilterCount === 0}
          className={styles.resetFiltersButton}
          renderIcon={FilterReset}
        >
          Reset Filters
        </Button>
      </div>
      <div className={styles.content}>{children}</div>
    </div>
  );
}
